/* المحادثات الخاصّة - منقولة من tpmsv33 كما صُمّمت. التخزين: Supabase + بث لحظي. */
import React, { useState, useEffect, useMemo, useRef } from "react";
import { toast } from "./toast.js";
import { supabase } from "./supabaseClient.js";
import { getOrgId } from "./api.js";
import { useAuth } from "./auth.jsx";

const fmtTime = (ms) => new Date(ms).toLocaleTimeString("ar", { hour: "2-digit", minute: "2-digit" });
const dayKey = (ms) => { const d = new Date(ms); return d.getFullYear() + "-" + d.getMonth() + "-" + d.getDate(); };
const dayLabel = (ms) => {
  const t = new Date(); const y = new Date(); y.setDate(t.getDate() - 1);
  if (dayKey(ms) === dayKey(t.getTime())) return "اليوم";
  if (dayKey(ms) === dayKey(y.getTime())) return "أمس";
  return new Date(ms).toLocaleDateString("ar");
};
const REACTS = ["", "", "", "", ""];
async function readChatFile(file) {
  const dataURL = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
  if (!/^image\//.test(file.type || "")) return { name: file.name, data: dataURL, type: file.type || "file" };
  return await new Promise((res) => {
    const img = new Image();
    img.onload = () => {
      const max = 1400; let w = img.width, h = img.height;
      if (w > max || h > max) { const r = Math.min(max / w, max / h); w = Math.round(w * r); h = Math.round(h * r); }
      const cv = document.createElement("canvas"); cv.width = w; cv.height = h; cv.getContext("2d").drawImage(img, 0, 0, w, h);
      let out; try { out = cv.toDataURL("image/jpeg", 0.82); } catch { out = dataURL; }
      res({ name: file.name, data: out, type: "image/jpeg" });
    };
    img.onerror = () => res({ name: file.name, data: dataURL, type: file.type || "image" });
    img.src = dataURL;
  });
}
const aBtn = { background: "none", border: "none", cursor: "pointer", fontSize: 14, padding: 0 };

const CSS = `
.ag-chat{display:flex;gap:10px;min-height:420px}
.ag-chat-list{width:200px;flex-shrink:0;display:flex;flex-direction:column;gap:4px;overflow-y:auto;max-height:540px}
.ag-chat-item{text-align:right;padding:8px 10px;border-radius:10px;border:1px solid var(--line);background:var(--surface);cursor:pointer;color:var(--text);font-family:inherit}
.ag-chat-item.on{border-color:var(--accent);background:color-mix(in srgb, var(--accent) 12%, var(--surface))}
.ag-mini{display:block;font-size:12px;color:var(--faint);margin-top:2px}
.ag-chat-main{flex:1;display:flex;flex-direction:column;border:1px solid var(--line);border-radius:12px;overflow:hidden;min-width:0}
.ag-chat-head{padding:8px 12px;border-bottom:1px solid var(--line);font-weight:700}
.ag-thread{flex:1;overflow-y:auto;display:flex;flex-direction:column;gap:8px;padding:12px;max-height:430px}
.ag-bubble{max-width:78%;padding:9px 13px;border-radius:0;font-size:14.5px;background:var(--surface);border:1px solid var(--line);box-shadow:3px 3px 0 color-mix(in srgb, var(--accent) 15%, transparent)}
.ag-bubble.mine{align-self:flex-start;background:color-mix(in srgb, var(--accent) 16%, var(--surface));border-color:color-mix(in srgb, var(--accent) 45%, transparent)}
.ag-bubble.other{align-self:flex-end}
.ag-file{font-size:12px;color:var(--faint)}
.ag-chat-input{display:flex;gap:6px;padding:8px;border-top:1px solid var(--line)}
.ag-chat-input>input[type="text"],.ag-chat-input>input:not([type]){flex:1;padding:8px 10px;border-radius:10px;border:1px solid var(--line);background:var(--surface);color:var(--text);font-family:inherit}
`;

function rowToMsg(r) {
  return { id: r.id, fromId: r.from_id, toId: r.to_id, text: r.text || "", attachments: r.attachments || [],
    replyTo: r.reply_to || null, reactions: r.reactions || {}, editedAt: r.edited_at ? Date.parse(r.edited_at) : null,
    deleted: !!r.deleted, at: Date.parse(r.created_at) };
}

export default function MessagesModal({ onClose }) {
  const auth = useAuth();
  const me = auth.me;
  const users = auth.users || [];
  const isManager = auth.realRole === "admin" || auth.role === "manager";
  const [messages, setMessages] = useState([]);
  const [qOpen, setQOpen] = useState(false);
  const [reads, setReads] = useState({});

  const load = async () => {
    try {
      const { data } = await supabase.from("messages").select("*").order("created_at");
      setMessages((data || []).map(rowToMsg));
      const r2 = await supabase.from("message_reads").select("*");
      const my = {}; (r2.data || []).forEach((x) => { my[x.peer_id] = Date.parse(x.last_read); });
      setReads({ [me.id]: my });
    } catch {}
  };
  useEffect(() => {
    load();
    let ch = null;
    (async () => {
      const org = await getOrgId(); if (!org) return;
      ch = supabase.channel("chat-" + org)
        .on("postgres_changes", { event: "*", schema: "public", table: "messages", filter: "org_id=eq." + org }, () => load())
        .subscribe();
    })();
    return () => { try { if (ch) supabase.removeChannel(ch); } catch {} };
  }, []);

  const sendMessage = async (fromId, toId, { text, attachments, replyTo }) => {
    const org = await getOrgId(); if (!org) return;
    const row = { id: crypto.randomUUID(), org_id: org, from_id: fromId, to_id: toId,
      text: text || null, attachments: attachments || [], reply_to: replyTo || null };
    const { error } = await supabase.from("messages").insert(row);
    if (error) { try { toast("تعذّر الإرسال: " + error.message, "err"); } catch {} return; }
    load();
  };
  const editMessage = async (id, text) => { await supabase.from("messages").update({ text, edited_at: new Date().toISOString() }).eq("id", id); load(); };
  const deleteMessage = async (id) => { await supabase.from("messages").update({ deleted: true }).eq("id", id); load(); };
  const [confirmPurge, setConfirmPurge] = useState(false);
  const purgeConversation = async () => {
    if (!sel) return;
    setConfirmPurge(false);
    try {
      await supabase.from("messages").delete().or(
        "and(from_id.eq." + me.id + ",to_id.eq." + sel + "),and(from_id.eq." + sel + ",to_id.eq." + me.id + ")"
      );
    } catch (e) { try { toast("تعذّر الحذف: " + (e && e.message || e), "err"); } catch {} }
    load();
  };
  const reactMessage = async (id, uid, emo) => {
    const m = messages.find((x) => x.id === id); if (!m) return;
    const rx = JSON.parse(JSON.stringify(m.reactions || {}));
    const arr = rx[emo] || [];
    rx[emo] = arr.includes(uid) ? arr.filter((x) => x !== uid) : [...arr, uid];
    await supabase.from("messages").update({ reactions: rx }).eq("id", id); load();
  };
  const markRead = async (meId, peer) => {
    const org = await getOrgId(); if (!org) return;
    try {
      await supabase.from("message_reads").upsert(
        { org_id: org, profile_id: meId, peer_id: peer, last_read: new Date().toISOString() },
        { onConflict: "profile_id,peer_id" });
    } catch {}
    setReads((prev) => ({ ...prev, [meId]: { ...(prev[meId] || {}), [peer]: Date.now() } }));
  };

  const [admins, setAdmins] = useState([]);
  useEffect(() => { if (isManager) return; (async () => {
    try { const { data } = await supabase.from("profiles").select("id, full_name").eq("role", "admin");
      setAdmins((data || []).map((a) => ({ id: a.id, fullName: a.full_name || "المدير", role: "manager", status: "active" }))); } catch {}
  })(); }, []);
  const contacts = isManager
    ? users.filter((u) => u.id !== me.id && u.status !== "deleted")
    : admins;
  const allPeople = [...users, ...admins];
  const nameOf = (id) => allPeople.find((u) => u.id === id)?.fullName ?? "";
  const myReads = reads?.[me.id] || {};
  const convMsgs = (peer) => messages.filter((m) => (m.fromId === me.id && m.toId === peer) || (m.fromId === peer && m.toId === me.id));
  const unreadFrom = (peer) => convMsgs(peer).filter((m) => m.fromId === peer && !m.deleted && m.at > (myReads[peer] || 0)).length;
  const lastOf = (peer) => { const a = convMsgs(peer).slice().sort((x, y) => x.at - y.at); return a.length ? a[a.length - 1] : null; };
  const sortedContacts = [...contacts].sort((a, b) => (lastOf(b.id)?.at || 0) - (lastOf(a.id)?.at || 0));

  const [sel, setSel] = useState(null);
  useEffect(() => { if (!sel && sortedContacts.length) setSel(sortedContacts[0].id); }, [users.length]);
  const [text, setText] = useState("");
  const [online, setOnline] = useState(() => { try { return new Set(window.__tpmsOnline || []); } catch { return new Set(); } });
  const [peerTyping, setPeerTyping] = useState(false);
  const typingTimer = useRef(null);
  const lastPing = useRef(0);
  const typingCh = useRef(null);
  useEffect(() => {
    const onOnline = () => { try { setOnline(new Set(window.__tpmsOnline || [])); } catch {} };
    window.addEventListener("pm:online", onOnline); onOnline();
    return () => window.removeEventListener("pm:online", onOnline);
  }, []);
  useEffect(() => {
    setPeerTyping(false);
    const ch = supabase.channel("tpms:typing");
    ch.on("broadcast", { event: "typing" }, ({ payload }) => {
      try {
        if (payload && payload.to === me.id && payload.from === sel) {
          setPeerTyping(true);
          if (typingTimer.current) clearTimeout(typingTimer.current);
          typingTimer.current = setTimeout(() => setPeerTyping(false), 2500);
        }
      } catch {}
    }).subscribe();
    typingCh.current = ch;
    return () => { try { supabase.removeChannel(ch); } catch {} };
  }, [me && me.id, sel]);
  const pingTyping = () => {
    try {
      const now = Date.now();
      if (now - lastPing.current < 900) return;
      lastPing.current = now;
      if (typingCh.current && sel) typingCh.current.send({ type: "broadcast", event: "typing", payload: { from: me.id, to: sel } });
    } catch {}
  };
  const [replyTo, setReplyTo] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [q, setQ] = useState("");
  const fileRef = useRef(null);
  const threadRef = useRef(null);

  const thread = useMemo(() => messages.filter((m) => (m.fromId === me.id && m.toId === sel) || (m.fromId === sel && m.toId === me.id)).sort((a, b) => a.at - b.at), [messages, me, sel]);
  const shown = q.trim() ? thread.filter((m) => (m.text || "").includes(q.trim())) : thread;
  const msgById = (id) => messages.find((m) => m.id === id);

  useEffect(() => { if (sel) markRead(me.id, sel); }, [sel, thread.length]);
  useEffect(() => { if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight; }, [sel, thread.length]);

  const send = () => { if (!sel || !text.trim()) return; sendMessage(me.id, sel, { text: text.trim(), attachments: [], replyTo: replyTo?.id || null }); setText(""); setReplyTo(null); };
  const onFiles = async (fl) => { if (!sel || !fl || !fl.length) return; const atts = []; for (const f of fl) atts.push(await readChatFile(f)); sendMessage(me.id, sel, { text: text.trim(), attachments: atts, replyTo: replyTo?.id || null }); setText(""); setReplyTo(null); };
  const saveEdit = () => { if (editingId) { editMessage(editingId, editText.trim()); setEditingId(null); setEditText(""); } };

  return (
    <div className="ag-modal-bg u4-bg" onClick={onClose}>
      <style>{CSS}</style>
      <div className="ag-modal wide u4 u4-win" onClick={(e) => e.stopPropagation()}>
        <div className="ag-modal-h"><span>المحادثات الخاصّة</span><button className="ag-btn sm" onClick={onClose}>إغلاق</button></div>
        <div className="ag-chat">
          <div className="ag-chat-list">
            {sortedContacts.length === 0 && <div className="ag-hint" style={{ padding: 10 }}>{isManager ? "أنشئ مستخدمين أولًا." : "لا يوجد مدير."}</div>}
            {sortedContacts.map((u) => {
              const un = unreadFrom(u.id); const lm = lastOf(u.id);
              const prev = lm ? (lm.deleted ? "حُذفت رسالة" : (lm.text || ((lm.attachments || []).length ? " مرفق" : ""))) : (u.role === "manager" ? "مدير" : "عضو");
              return (
                <button key={u.id} className={"ag-chat-item" + (sel === u.id ? " on" : "")} onClick={() => { setSel(u.id); setQ(""); }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>{online.has(u.id) && <span className="tp-pulse" style={{ width: 8, height: 8, borderRadius: 99, background: "#10b981", display: "inline-block", flexShrink: 0 }} />}{u.fullName}</span>
                    {un > 0 && <span style={{ background: "var(--accent)", color: "#fff", borderRadius: 999, fontSize: 14, fontWeight: 800, minWidth: 20, textAlign: "center", padding: "0 6px" }}>{un}</span>}
                  </div>
                  <span className="ag-mini">{String(prev).slice(0, 30)}</span>
                </button>
              );
            })}
          </div>
          <div className="ag-chat-main">
            {sel && <button className="ag-chat-back" aria-label="عودة" onClick={() => setSel(null)}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg><span className="ag-back-txt">عودة للجهات</span></button>}
            {!sel ? <div className="ag-empty">اختر شخصًا.</div> : (
              <>
                <div className="ag-chat-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>{online.has(sel) && <span className="tp-pulse" style={{ width: 8, height: 8, borderRadius: 99, background: "#10b981", display: "inline-block" }} />}{nameOf(sel)}{peerTyping && <span className="tp-typing"><i /><i /><i /></span>}</span>
                  <span className="ag-head-tools" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  {qOpen ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث…" className="ag-head-q" style={{ fontSize: 13.5, padding: "7px 12px", borderRadius: 17, border: "1px solid var(--line)", background: "color-mix(in srgb, var(--surface) 70%, transparent)", color: "var(--text)", width: 150 }} />
                      <button className="ag-ico-btn" aria-label="إغلاق البحث" onClick={() => { setQOpen(false); setQ(""); }}><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
                    </span>
                  ) : (
                    <button className="ag-ico-btn" aria-label="بحث" onClick={() => setQOpen(true)}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.5" y2="16.5"/></svg></button>
                  )}
                  {isManager && <button className="ag-ico-btn danger" aria-label="حذف المحادثة" onClick={() => setConfirmPurge(true)}><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg></button>}
                  </span>
                  
                  {confirmPurge && (
                    <div style={{ position: "fixed", inset: 0, zIndex: 1200, background: "rgba(0,0,0,.55)", display: "flex", alignItems: "center", justifyContent: "center", direction: "rtl" }} onClick={() => setConfirmPurge(false)}>
                      <div className="tp-zoom" style={{ width: "min(430px, 92vw)", background: "var(--surface)", border: "1.5px solid #e23d3d", borderRadius: 16, padding: "22px 24px", boxShadow: "0 16px 48px rgba(0,0,0,.5)" }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ fontSize: 17, fontWeight: 800, color: "#e23d3d", marginBottom: 10 }}> حذف المحادثة نهائيًا</div>
                        <div style={{ fontSize: 14.5, lineHeight: 1.8, color: "var(--text)", marginBottom: 18 }}>ستُحذف محادثة «{nameOf(sel)}» كاملة من السحابة - كل الرسائل والمرفقات، للطرفين. لا رجعة في هذا الإجراء.</div>
                        <div style={{ display: "flex", gap: 10, justifyContent: "flex-start" }}>
                          <button onClick={purgeConversation} style={{ fontSize: 14, fontWeight: 800, padding: "9px 20px", borderRadius: 10, border: "none", background: "#e23d3d", color: "#fff", cursor: "pointer" }}>حذف نهائي</button>
                          <button onClick={() => setConfirmPurge(false)} style={{ fontSize: 14, fontWeight: 700, padding: "9px 20px", borderRadius: 10, border: "1px solid var(--line)", background: "transparent", color: "var(--text)", cursor: "pointer" }}>إلغاء</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="ag-thread" ref={threadRef}>
                  {shown.length === 0 && <div className="ag-empty">{q ? "لا نتائج." : "لا رسائل بعد."}</div>}
                  {shown.map((m, i) => {
                    const mine = m.fromId === me.id;
                    const prev = shown[i - 1];
                    const showDay = !prev || dayKey(prev.at) !== dayKey(m.at);
                    const rep = m.replyTo ? msgById(m.replyTo) : null;
                    const grouped = !showDay && prev && prev.fromId === m.fromId && (m.at - prev.at) < 300000;
                    const isSys = !m.deleted && (m.text || "").startsWith("اعتُمد وخُتم");
                    return (
                      <React.Fragment key={m.id}>
                        {showDay && <div style={{ alignSelf: "center", fontSize: 14, color: "var(--faint)", margin: "6px 0" }}>{dayLabel(m.at)}</div>}
                        <div className={"ag-bubble " + (isSys ? "sys" : (mine ? "mine" : "other"))} style={{ position: "relative", marginTop: grouped ? 2 : 10 }} onClick={(e) => { if (window.innerWidth <= 768) e.currentTarget.classList.toggle("show-acts"); }}>
                          {rep && <div style={{ paddingInlineStart: 8, opacity: 0.8, fontSize: 14, marginBottom: 5 }}>{(rep.deleted ? "رسالة محذوفة" : (rep.text || "مرفق")).slice(0, 60)}</div>}
                          {m.deleted ? <div style={{ opacity: 0.6, fontStyle: "italic" }}> حُذفت هذه الرسالة</div> : (
                            editingId === m.id ? (
                              <div style={{ display: "flex", gap: 6 }}>
                                <input value={editText} onChange={(e) => setEditText(e.target.value)} style={{ flex: 1, fontSize: 14, padding: "4px 8px", borderRadius: 8, border: "1px solid var(--line)", color: "var(--text)", background: "color-mix(in srgb, var(--surface) 80%, transparent)" }} onKeyDown={(e) => e.key === "Enter" && saveEdit()} />
                                <button className="ag-btn xs" onClick={saveEdit}>حفظ</button>
                              </div>
                            ) : (
                              <>
                                {m.text && <div>{m.text}</div>}
                                {(m.attachments || []).map((a, ai) => (
                                  /^image\//.test(a.type)
                                    ? <a key={ai} href={a.data} download={a.name} target="_blank" rel="noreferrer"><img src={a.data} alt={a.name} style={{ maxWidth: "100%", borderRadius: 8, marginTop: 5, display: "block" }} /></a>
                                    : <a key={ai} href={a.data} download={a.name} target="_blank" rel="noreferrer" className="ag-file" style={{ display: "block", color: "inherit" }}> {a.name}</a>
                                ))}
                                {m.editedAt && <div className="ag-file">(عُدّلت)</div>}
                              </>
                            )
                          )}
                          <div className="ag-file" style={{ marginTop: 3, opacity: 0.55, fontSize: 11 }}>{fmtTime(m.at)}</div>
                          {!m.deleted && editingId !== m.id && (
                            <div className="ag-acts" style={{ display: "flex", gap: 8, marginTop: 5, flexWrap: "wrap", alignItems: "center" }}>
                              <button onClick={() => setReplyTo(m)} style={{ ...aBtn, color: "var(--accent)" }}>ردّ</button>
                              {mine && <button onClick={() => { setEditingId(m.id); setEditText(m.text || ""); }} style={{ ...aBtn, color: "var(--accent)" }}>تعديل</button>}
                              {isManager && <button onClick={() => deleteMessage(m.id)} style={{ ...aBtn, color: "#e23d3d" }}>حذف</button>}
                            </div>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
                {replyTo && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 10px", background: "color-mix(in srgb, var(--accent) 12%, transparent)", borderRadius: 8, margin: "0 10px 6px", fontSize: 14 }}>
                    <span>ردّ على: {(replyTo.text || "مرفق").slice(0, 40)}</span>
                    <button className="ag-btn xs" onClick={() => setReplyTo(null)}>×</button>
                  </div>
                )}
                <div className="ag-chat-input" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files && e.dataTransfer.files.length) onFiles(e.dataTransfer.files); }}>
                  <input value={text} onChange={(e) => { setText(e.target.value); pingTyping(); }} placeholder="اكتب رسالة…" onKeyDown={(e) => e.key === "Enter" && send()} />
                  <input ref={fileRef} type="file" multiple hidden onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }} />
                  <button className="ag-ico-btn" aria-label="إرفاق ملف" onClick={() => fileRef.current && fileRef.current.click()}><svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg></button>
                  <button className="ag-send-btn" aria-label="إرسال" onClick={send}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: "scaleX(-1)" }}><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg></button>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="ag-hint" style={{ marginTop: 8 }}>كل محادثة معزولة بين الطرفين وحدهما - والرسائل تصل فورًا عبر السحابة.</div>
      </div>
    </div>
  );
}
